import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Tooltip, IconButton, Popover, Typography, Button } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation } from 'react-router-dom';
import styles from './FooterHelp.module.css';

// Configuration object that maps component IDs to their specific configurations
const COMPONENT_CONFIGS = {
  dashboard: { // Matches the id used in FooterHelp in Dashboard
    routes: [
      {
        // href: '/mediarelations',
        // showHelpButton: false,
      },
      // Add more routes specific to dashboard
    ]
  },
};

const COLLAPSED_STATE_KEY = 'gsd-help-button-collapsed';
const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
const modifierKey = isMac ? '⌘' : 'Ctrl';
const shortcutKey = 'H';
const shortcutDisplay = `${modifierKey}+${shortcutKey}`;

const FooterHelp = ({ routeConfig, id }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [isAnimating, setIsAnimating] = useState(true);
    const [showHelpButton, setShowHelpButton] = useState(true);
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return localStorage.getItem(COLLAPSED_STATE_KEY) === 'true';
    });
    const [showCollapseButton, setShowCollapseButton] = useState(false);
    const hideButtonTimerRef = useRef(null);
    const location = useLocation();
    const effectiveRouteConfig = useMemo(() => {
        return routeConfig || (id && COMPONENT_CONFIGS[id]?.routes) || [];
    }, [routeConfig, id]);

    useEffect(() => {
        const currentPath = location.pathname;
        const matchingRoute = effectiveRouteConfig.find(route => 
            (currentPath.startsWith(route.href) && route.showHelpButton === false) ||
            (currentPath.startsWith(route.href) && route.showHelp === false)
        );
        setShowHelpButton(!matchingRoute);
    }, [location.pathname, effectiveRouteConfig]);

    useEffect(() => {
        let animationStartTimer;
        let isMounted = true;
        animationStartTimer = setTimeout(() => {
            if (isMounted) setIsAnimating(true);
        }, 500);
        return () => {
            isMounted = false;
            clearTimeout(animationStartTimer);
        };
    }, []);

    useEffect(() => {
        localStorage.setItem(COLLAPSED_STATE_KEY, isCollapsed);
    }, [isCollapsed]);

    const handleHelpClick = useCallback((event) => {
        if (!isCollapsed) {
            setAnchorEl(event.currentTarget);
        }
    }, [isCollapsed]);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const toggleCollapse = useCallback(() => {
        setIsCollapsed(!isCollapsed);
        setAnchorEl(null); 
    }, [isCollapsed]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((isMac && e.metaKey && e.key === 'h') || 
                (!isMac && e.ctrlKey && e.key === 'h')) {
                e.preventDefault();
                if (isCollapsed) {
                    toggleCollapse();
                } else {
                    if (anchorEl) {
                        handleClose();
                    } else {
                        const helpButton = document.getElementById('helpButton');
                        if (helpButton) {
                            handleHelpClick({ currentTarget: helpButton });
                        }
                    }
                }
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isCollapsed, anchorEl, handleHelpClick, toggleCollapse, handleClose]);

    const handleSupport = () => {
        window.open(
            'https://meltwater.slack.com/archives/C066HEYFYH5',
            '_blank',
            'noopener,noreferrer'
        );
        handleClose();
    };

    const handleHelpHover = () => {
        if (hideButtonTimerRef.current) {
            clearTimeout(hideButtonTimerRef.current);
            hideButtonTimerRef.current = null;
        }
        setShowCollapseButton(true);
    };

    const handleHelpLeave = () => {
        hideButtonTimerRef.current = setTimeout(() => {
            setShowCollapseButton(false);
            hideButtonTimerRef.current = null;
        }, 500);
    };

    const handleCollapseButtonHover = () => {
        if (hideButtonTimerRef.current) {
            clearTimeout(hideButtonTimerRef.current);
            hideButtonTimerRef.current = null;
        }
    };

    const handleCollapseButtonLeave = () => {
        hideButtonTimerRef.current = setTimeout(() => {
            setShowCollapseButton(false);
            hideButtonTimerRef.current = null;
        }, 500);
    };

    useEffect(() => {
        return () => {
            if (hideButtonTimerRef.current) {
                clearTimeout(hideButtonTimerRef.current);
            }
        };
    }, []);

    const open = Boolean(anchorEl);

    if (!showHelpButton) {
        return null;
    }

    return (
        <div 
            className={`${styles.helpButtonContainer} ${isCollapsed ? styles.collapsed : ''}`}
            role="complementary" 
            aria-label="Help and support"
        >
            {isCollapsed ? (
                <Tooltip 
                    title={
                        <div className={styles.tooltipCompact}>
                            Show help button
                        </div>
                    }
                    placement="left"
                    arrow
                    className={styles.tooltip}
                >
                    <IconButton
                        className={styles.expandButton}
                        onClick={toggleCollapse}
                        aria-label="Expand help button"
                        data-testid="expand-help-button"
                        aria-expanded="false"
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <>
                    <Tooltip 
                        title={
                            <div className={styles.tooltipCompact}>
                                Help options ({shortcutDisplay})
                            </div>
                        }
                        placement="left"
                        open={!open && !showCollapseButton}
                        arrow
                        className={styles.tooltip}
                    >
                        <IconButton
                            id="helpButton"
                            className={`${styles.helpButton} ${isAnimating ? styles.helpButtonAnimate : ''}`}
                            onClick={handleHelpClick}
                            onMouseEnter={handleHelpHover}
                            onMouseLeave={handleHelpLeave}
                            style={{ backgroundColor: '#188182', color: 'white' }}
                            aria-label="Help options"
                            aria-haspopup="true"
                            aria-expanded={open}
                            data-testid="help-button"
                        >
                            <HelpIcon fontSize="large" />
                        </IconButton>
                    </Tooltip>
                    
                    {showCollapseButton && (
                        <Tooltip
                            title={
                                <div className={styles.tooltipCompact}>
                                    Hide help button
                                </div>
                            }
                            placement="left"
                            arrow
                        >
                            <IconButton
                                className={styles.collapseButton}
                                onClick={toggleCollapse}
                                onMouseEnter={handleCollapseButtonHover}
                                onMouseLeave={handleCollapseButtonLeave}
                                aria-label="Collapse help button"
                                data-testid="collapse-help-button"
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </>
            )}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                className={styles.helpPopover}
                id="help-popover"
                role="dialog"
                aria-label="Help options dialog"
            >
                <div className={styles.helpContent}>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        className={styles.closeButton}
                        aria-label="Close help dialog"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h6" className={styles.helpTitle}>
                        Need Help?
                    </Typography>
                    <Typography variant="body1" className={styles.helpText}>
                        • Request new features<br/>
                        • Report issues<br/>
                        • Get support with GSD tools<br/>
                        • Ask questions
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleSupport}
                        className={styles.supportButton}
                        aria-label="Go to support channel on Slack"
                    >
                        Go to Support Channel
                    </Button>
                    <Typography variant="caption" className={styles.keyboardShortcut}>
                        Keyboard shortcut: {shortcutDisplay}
                    </Typography>
                </div>
            </Popover>
        </div>
    );
};

export default FooterHelp;